import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTokenValid, setIsTokenValid] = useState(true);
    const [email, setEmail] = useState('');
    const [passwordToken, setPasswordToken] = useState('');

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Extract token and email from URL
        const params = new URLSearchParams(location.search);
        const urlToken = params.get('token');
        const urlEmail = params.get('email');

        if (!urlToken || !urlEmail) {
            setIsTokenValid(false);
            setError('Invalid password reset link. Please request a new one.');
            return;
        }

        setPasswordToken(urlToken);
        setEmail(urlEmail);
    }, [location]);

    const validatePassword = (password) => {
        if (password.length < 8) return false;
        if (!/[A-Z]/.test(password)) return false;
        if (!/[a-z]/.test(password)) return false;
        if (!/[0-9]/.test(password)) return false;
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return false;
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setIsSubmitting(true);

        if (!password || !confirmPassword) {
            setError('Please enter both password fields');
            setIsSubmitting(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setIsSubmitting(false);
            return;
        }

        if (!validatePassword(password)) {
            setError('Password must be at least 8 characters long and include uppercase and lowercase letters, numbers, and special characters');
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, passwordToken, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'An error occurred');
            }

            setMessage(data.message || 'Your password has been reset successfully');

            // Store the email in localStorage before redirecting
            localStorage.setItem('resetEmail', email);

            // Redirect to sign in page after 3 seconds
            setTimeout(() => {
                navigate('/signin');
            }, 3000);
        } catch (error) {
            setError(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isTokenValid) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
                <div className="px-8 py-6 mt-4 text-left bg-white dark:bg-gray-800 shadow-lg rounded-lg w-full max-w-md">
                    <h3 className="text-2xl font-bold text-center text-gray-800 dark:text-white">Invalid Reset Link</h3>
                    <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
                        {error}
                    </div>
                    <div className="flex justify-center mt-6">
                        <Link to="/forgot-password" className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-900">
                            Request a new reset link
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
            <div className="px-8 py-6 mt-4 text-left bg-white dark:bg-gray-800 shadow-lg rounded-lg w-full max-w-md">
                <h3 className="text-2xl font-bold text-center text-gray-800 dark:text-white">Reset Password</h3>
                <p className="mt-2 text-center text-gray-600 dark:text-gray-400">
                    Enter your new password below
                </p>

                {message && (
                    <div className="mt-4 p-3 bg-green-100 text-green-700 rounded">
                        {message}
                    </div>
                )}

                {error && (
                    <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="mt-4">
                    <div className="mt-4">
                        <label className="block text-gray-700 dark:text-gray-200" htmlFor="password">New Password</label>
                        <input
                            type="password"
                            placeholder="New Password"
                            className="w-full px-4 py-2 mt-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="block text-gray-700 dark:text-gray-200" htmlFor="confirmPassword">Confirm New Password</label>
                        <input
                            type="password"
                            placeholder="Confirm New Password"
                            className="w-full px-4 py-2 mt-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                    </div>

                    <div className="flex justify-center mt-6">
                        <button
                            type="submit"
                            className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 disabled:opacity-50"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword; 